/**
 * OAuth 2.0 authorize hand-off — the page browser hosts land on.
 *
 * chrome.identity.launchWebAuthFlow (the Chrome extension) and desktop apps
 * open GET /oauth/authorize as a plain navigation, which cannot carry the
 * session header the API requires. The API sends such navigations here with
 * the query intact; this page holds the session, calls the API back with the
 * header and `response_mode=json`, and navigates to the `redirect_to` it gets
 * (the consent screen, or straight back to the app for a trusted client).
 * Signed-out visitors are sent to /login with this URL as `?next=`.
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API_URL } from '../../config/api';
import { getAuthToken } from '../../services/api/core';
import { AuthFormCard, AuthPageLayout } from '@/layouts/AuthPageLayout';
import { t } from '../../i18n';
import { buildAuthorizeApiUrl } from './authorizeUrl';

type Status = 'working' | 'error';

interface RedirectResponse {
  redirect_to?: string;
  error?: string;
  error_description?: string;
}

const Authorize: React.FC = () => {
  const [params] = useSearchParams();
  const [status, setStatus] = React.useState<Status>('working');
  const [error, setError] = React.useState<string | null>(null);
  // The hand-off navigates exactly once. Development StrictMode runs the
  // effect twice, and a second navigation aborts the first — which
  // chrome.identity treats as the page failing to load and ends the whole
  // sign-in ("Authorization page could not be loaded").
  const navigated = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    const go = (to: string): void => {
      if (navigated.current) return;
      navigated.current = true;
      window.location.assign(to);
    };
    const clientId = params.get('client_id');
    const redirectUri = params.get('redirect_uri');
    if (!clientId || !redirectUri) {
      setStatus('error');
      setError(t('oauthAuthorize.missingParams'));
      return;
    }
    const token = getAuthToken();
    if (!token) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      go(`/login?next=${next}`);
      return;
    }
    (async () => {
      try {
        const res = await fetch(buildAuthorizeApiUrl(API_URL, window.location.search), {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        });
        const body = (await res.json()) as RedirectResponse;
        if (cancelled) return;
        if (!res.ok || typeof body.redirect_to !== 'string') {
          setStatus('error');
          setError(body.error_description ?? body.error ?? t('oauthAuthorize.failed'));
          return;
        }
        go(body.redirect_to);
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setError(err instanceof Error ? err.message : t('oauthAuthorize.failed'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params]);

  return (
    <AuthPageLayout className="bg-white">
      <AuthFormCard className="shadow-none">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">{t('oauthAuthorize.title')}</h1>
        {status === 'working' ? (
          <p className="text-sm text-gray-600" role="status">{t('oauthAuthorize.working')}</p>
        ) : (
          <>
            <div className="mb-6 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-800" role="alert">
              {error}
            </div>
            <Link to="/" className="text-sm text-gray-900 underline underline-offset-2">
              {t('oauthAuthorize.backToApp')}
            </Link>
          </>
        )}
      </AuthFormCard>
    </AuthPageLayout>
  );
};

export default Authorize;
